import { Box, Card, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { AppUtils } from "source/framework/appUtils";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import * as appReportService from "../../../models/services/appReportService";
import { ActionContainer } from "../../framework/ActionContainer";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogButtons } from "../../framework/Dialog/DialogButtons";
import { GridRow } from "../../framework/Grid/GridRow";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { getFirstSelectableTreeListItem, ITreeListItem, TreeList } from "source/components/framework/List/TreeList";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { IBaseFormModel } from "ui/Scripts/source/models/common/baseFormModel";
import { AppReportTemplateFormModel, IAppReportTemplateFormModel } from "ui/Scripts/source/models/appReportTemplate/appReportTemplateFormModel";
import { TaskTypeDialogPreviewType, TaskTypeDialogPreviewWeb } from "source/components/task/TaskTypeDialog/TaskTypeDialogPreview/TaskTypeDialogPreviewWeb";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";

interface IAppReportTemplateNewDialogProps {
    companyId: string;
    onOk: (id: string) => void;
    onCancel: () => void;
}

interface IAppReportTemplateNewDialogState {
    allSystemAppReportTemplates: IAppReportTemplateFormModel[];
    allCompanyAppReportTemplates: IAppReportTemplateFormModel[];
    listItems: ITreeListItem[];
    appReportTemplate: IAppReportTemplateFormModel;
    taskType: ITaskEditTypeItem;
    selectedId: string;
    filter: string;
    formDataRow: ITaskDataRow;
    initialized: boolean;
    isLoading: boolean;
}

export const AppReportTemplateNewDialog = (
    props: IAppReportTemplateNewDialogProps
) => {
    const createEmptyAppReportTemplateFormModel = () => {
        const result = new AppReportTemplateFormModel();
        result.id = Base.emptyGuid;
        result.code = `'${Translations.EmptyReport}'`;
        return result;
    };

    const getDataRows = (formModel: IBaseFormModel) => {
        const formDataRow = new TaskDataRow();
        if (formModel) {
            formDataRow.cols = TaskDataRowCol.initializeValuesByType(formModel.formCols, []);
        }
        return {
            formDataRow
        };
    };

    const [state, setState] = useState<IAppReportTemplateNewDialogState>(() => {
        const appReportTemplate = createEmptyAppReportTemplateFormModel();
        return {
            allSystemAppReportTemplates: [createEmptyAppReportTemplateFormModel()],
            allCompanyAppReportTemplates: [],
            listItems: [],
            appReportTemplate,
            taskType: new TaskEditTypeItem(appReportTemplate),
            selectedId: Base.emptyGuid,
            filter: "",
            ...getDataRows(createEmptyAppReportTemplateFormModel()),
            initialized: false,
            isLoading: false,
        };
    });
    
    const getSubTreeListItems = (appReportTemplates: IAppReportTemplateFormModel[]) => {
        return appReportTemplates.map(item => {
            return {
                id: item.id,
                title: item.code,
                children: []
            };
        });
    };

    const getTreeListItems = (allSystemAppReportTemplates: IAppReportTemplateFormModel[], allCompanyAppReportTemplates: IAppReportTemplateFormModel[], searchFilter: string): ITreeListItem[] => {
        const result: ITreeListItem[] = [];
        const lowSearchFilter = searchFilter.toLocaleLowerCase();
        // Add empty app report template to the top of the list
        const emptyAppReportTemplate = createEmptyAppReportTemplateFormModel();
        if (!lowSearchFilter || emptyAppReportTemplate.code.toLocaleLowerCase().indexOf(lowSearchFilter) > -1) {
            result.push({ id: emptyAppReportTemplate.id, title: emptyAppReportTemplate.code, children: [] });
        }
        // Add company app report templates
        const companyAppReportTemplates = lowSearchFilter
            ? allCompanyAppReportTemplates.filter(i => i.code.toLocaleLowerCase().indexOf(searchFilter) > -1)
            : allCompanyAppReportTemplates.slice(0);
        if (companyAppReportTemplates.length > 0) {
            result.push({
                id: Translations.ReportTemplates,
                title: Translations.ReportTemplates,
                children: getSubTreeListItems(companyAppReportTemplates)
            });
        }
        // Add system app report templates
        const systemAppReportTemplates = lowSearchFilter
            ? allSystemAppReportTemplates.filter(i => i.code.toLocaleLowerCase().indexOf(searchFilter) > -1)
            : allSystemAppReportTemplates.slice(0);
        if (systemAppReportTemplates.length > 0) {
            result.push({
                id: Translations.SharedReportTemplates,
                title: Translations.SharedReportTemplates,
                children: getSubTreeListItems(systemAppReportTemplates)
            });
        }
        return result;
    };

    const loadItems = async () => {
        setState((old) => ({ ...old, isLoading: true }));
        const result = await AppUtils.callService(() => appReportService.getAppReportTemplateNew(props.companyId), false, false, () => setState((old) => ({ ...old, isLoading: false })));
        if (!result) {
            props.onCancel();
            return;
        }
        const allSystemReportTemplates = result.systemReportTemplates;
        const allCompanyReportTemplates = result.companyReportTemplates;
        const listItems = getTreeListItems(allSystemReportTemplates, allCompanyReportTemplates, state.filter);
        const selectedId = getFirstSelectableTreeListItem(listItems, Base.emptyGuid)?.id ?? state.selectedId;
        const appReportTemplate = allSystemReportTemplates.union(allCompanyReportTemplates).find(i => i.id === selectedId) ?? state.appReportTemplate;
        setState((old) => ({
            ...old,
            initialized: true,
            allSystemAppReportTemplates: allSystemReportTemplates,
            allCompanyAppReportTemplates: allCompanyReportTemplates,
            listItems,
            appReportTemplate,
            taskType: new TaskEditTypeItem(appReportTemplate),
            selectedId: appReportTemplate?.id ?? "",
            ...getDataRows(appReportTemplate)
        }));
    };

    useEffect(() => {
        loadItems();
    }, []);

    const changeFilter = (searchFilter: string) => {
        let systemReportTemplates: IAppReportTemplateFormModel[] = [];
        let companyReportTemplates: IAppReportTemplateFormModel[] = [];
        if (searchFilter) {
            const lowSearchFilter = searchFilter.toLocaleLowerCase();
            systemReportTemplates = state.allSystemAppReportTemplates.filter(i => i.code.toLocaleLowerCase().indexOf(lowSearchFilter) > -1);
            companyReportTemplates = state.allCompanyAppReportTemplates.filter(i => i.code.toLocaleLowerCase().indexOf(lowSearchFilter) > -1);
        } else {
            systemReportTemplates = state.allSystemAppReportTemplates.slice(0);
            companyReportTemplates = state.allCompanyAppReportTemplates.slice(0);
        }
        const appReportTemplate = systemReportTemplates.concat(companyReportTemplates).find(i => i.id === state.selectedId);
        setState((old) => ({
            ...old,
            filter: searchFilter,
            appReportTemplate,
            selectedId: appReportTemplate ? state.selectedId : "",
            ...getDataRows(appReportTemplate),
            listItems: getTreeListItems(state.allSystemAppReportTemplates, state.allCompanyAppReportTemplates, searchFilter)
        }));
    };

    const handleSetSelectedId = (selectedId: string) => {
        const appReportTemplate = state.allSystemAppReportTemplates.concat(state.allCompanyAppReportTemplates).find(i => i.id === selectedId);
        setState((old) => ({
            ...old,
            selectedId,
            appReportTemplate,
            taskType: new TaskEditTypeItem(appReportTemplate),
            ...getDataRows(appReportTemplate)
        }));
    };

    return (
        <Dialog
            id="appReportTemplateNewDialog"
            maxWidth="xl"
            maxHeight="100%"
            title={Translations.AddReportTemplateFromTemplate}
            body={
                <GridRow height="100%" sx={{ height: "calc(100% + 16px)" }}>
                    <Grid item sm={4} height="100%">
                        <VerticalContainer
                            before={
                                <ActionContainer
                                    leftItems={
                                        <ListSearchFilter
                                            searchFilter={state.filter}
                                            onSearchClick={changeFilter}
                                        />
                                    }
                                />
                            }
                            main={
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        height: "100%",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderRadius: ".5rem",
                                            height: "100%",
                                            overflowY: "auto"
                                        }}
                                    >
                                        <TreeList
                                            items={state.listItems}
                                            selectedId={state.selectedId}
                                            onSelect={item => handleSetSelectedId(item.id)}
                                        />
                                    </Box>
                                </Card>
                            }
                        />
                    </Grid>
                    <Grid item sm={8} height="100%">
                        <TaskTypeDialogPreviewWeb
                            previewType={TaskTypeDialogPreviewType.Web}
                            height="100%"
                            title={Translations.Preview}
                            taskType={state.taskType}
                            headerDataRow={null}
                            formDataRow={state.formDataRow}
                            lineDataRow={null}
                            onFormItemClick={() => {}}
                            onHeaderItemClick={() => {}}
                            onUpdateFormDataRowCols={() => {}}
                            onUpdateHeaderDataRowCols={() => {}}
                        />
                    </Grid>
                </GridRow>
            }
            actions={
                <DialogButtons
                    saveDisabled={!state.selectedId}
                    saveTitle={Translations.Next}
                    onCancel={props.onCancel}
                    onSave={() => Promise.resolve(props.onOk(state.selectedId))}
                />
            }
            onClose={props.onCancel}
        />
    );
};
